<template>
    <v-dialog v-model="internalDialog" persistent max-width="640" scrollable content-class="sod-eod-dialog eod-dialog-content">
        <v-card class="sod-eod-card">
            <template v-if="showEarlyConfirm">
                <v-card-title class="sod-eod-title px-6 pt-6 pb-0">End of Day</v-card-title>
                <v-card-text class="pt-4">
                    It's earlier than your usual end of day — submit anyway?
                </v-card-text>
                <v-card-actions class="sod-eod-actions">
                    <v-spacer></v-spacer>
                    <v-btn @click="showEarlyConfirm = false" class="sod-eod-btn-secondary" depressed>Not yet</v-btn>
                    <v-btn @click="confirmEarly" class="sod-eod-btn-primary" depressed>Submit Anyway</v-btn>
                </v-card-actions>
            </template>

            <template v-else>
                <div class="sod-eod-header">
                    <div class="sod-eod-badge eod-badge">🌙</div>
                    <div class="sod-eod-heading">
                        <div class="sod-eod-title">{{ dialogTitle }}</div>
                        <div class="sod-eod-subtitle">How did today go?</div>
                    </div>
                    <button type="button" class="sod-eod-close" @click="dismiss" aria-label="Close">
                        <v-icon small>mdi-close</v-icon>
                    </button>
                </div>

                <v-card-text class="sod-eod-body">
                    <v-alert v-if="supersededNotice" dense outlined type="warning" class="mb-4">
                        A newer End of Day submission already exists (made from another tab or device).
                        Showing that submission below — resubmitting will update it further.
                    </v-alert>

                    <v-progress-circular v-if="loading" indeterminate color="#2E9E5B" class="d-block mx-auto my-6" />

                    <template v-else>
                        <div class="sod-eod-info-box eod-info-box">
                            <div class="d-flex align-center justify-space-between">
                                <div class="eod-progress-count">
                                    {{ completedCount }} / {{ plannedCount }} planned tasks completed
                                </div>
                                <div class="eod-progress-percent">{{ progressPercent }}%</div>
                            </div>
                            <v-progress-linear :value="progressPercent" height="8" rounded color="#2E9E5B"
                                background-color="#D6E4F5" class="mt-2" />
                        </div>

                        <div v-if="plannedTasks.length" class="eod-task-list mb-4">
                            <div v-for="task in plannedTasks" :key="task.task_id" class="eod-task-row"
                                @click="toggleTaskCompletion(task)">
                                <span class="eod-task-dot" :class="{ done: task.is_completed }">
                                    <v-icon v-if="task.is_completed" x-small color="white">mdi-check</v-icon>
                                </span>
                                <span class="text-body-2 eod-task-name">{{ task.name }}</span>
                                <span class="text-caption grey--text">{{ formatTrackedTime(task.tracked_seconds) }}</span>
                            </div>
                        </div>
                        <div v-else class="text-body-2 grey--text mb-4">
                            No planned tasks found for today.
                        </div>

                        <v-form ref="eodForm">
                            <template v-if="!allTasksCompleted">
                                <div class="sod-eod-info-title mb-2">
                                    {{ incompleteReasonQuestion }}
                                </div>
                                <v-radio-group v-model="incompleteReason" hide-details class="mt-0 mb-2 eod-reason-group">
                                    <v-radio v-for="opt in incompleteReasonOptions" :key="opt.value" :value="opt.value"
                                        :label="opt.text" color="#2E9E5B" />
                                </v-radio-group>
                                <v-text-field v-if="incompleteReason === 'other'" v-model="incompleteReasonOther"
                                    label="Please specify" outlined dense rounded class="mb-2" />
                            </template>

                            <v-row class="mt-2" dense>
                                <v-col cols="12" sm="6">
                                    <div class="sod-eod-info-title mb-2">Biggest accomplishment today</div>
                                    <v-textarea v-model="biggestAccomplishment" outlined dense rounded rows="2"
                                        hide-details />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <div class="sod-eod-info-title mb-2">Anything your manager should know?</div>
                                    <v-textarea v-model="managerNote" outlined dense rounded rows="2" hide-details />
                                </v-col>
                            </v-row>

                            <div class="sod-eod-info-title mt-4 mb-2">Workload today</div>
                            <div class="eod-pulse-row">
                                <button v-for="opt in workloadPulseOptions" :key="opt.value" type="button"
                                    class="eod-pulse-option" :class="{ selected: workloadPulse === opt.value }"
                                    @click="workloadPulse = opt.value">
                                    <span class="eod-pulse-emoji">{{ opt.emoji }}</span>
                                    <span>{{ opt.text }}</span>
                                </button>
                            </div>
                        </v-form>
                    </template>
                </v-card-text>

                <v-card-actions class="sod-eod-actions">
                    <v-btn v-if="isAdmin" @click="clearData" :loading="clearing"
                        :disabled="loading || saving || clearing" text color="error" class="text-none">
                        Clear Data
                    </v-btn>
                    <v-btn @click="viewHistory" :disabled="saving || clearing" text color="primary" class="text-none">
                        View History
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn @click="dismiss" :disabled="saving || clearing" class="sod-eod-btn-secondary" depressed>
                        {{ cancelLabel }}
                    </v-btn>
                    <v-btn @click="handleSubmitClick" :loading="saving" :disabled="loading || clearing"
                        class="sod-eod-btn-primary" depressed>
                        Submit EOD
                    </v-btn>
                </v-card-actions>
            </template>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'EodDialog',
    inject: {
        todayEodPrefill: { from: 'todayEodPrefill', default: () => () => null },
        refreshTodayEodPrefill: { from: 'refreshTodayEodPrefill', default: () => async () => null },
        sodEodStatus: { from: 'sodEodStatus', default: () => () => null },
        submitEodAction: { from: 'submitEod', default: () => async () => { throw new Error('submitEod not provided'); } },
        clearEodAction: { from: 'clearEod', default: () => async () => { throw new Error('clearEod not provided'); } },
        findTaskInArrays: { from: 'findTaskInArrays', default: () => () => null },
        optimisticUpdateTask: { from: 'optimisticUpdateTask', default: () => async () => { throw new Error('optimisticUpdateTask not provided'); } },
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            clearing: false,
            incompleteReason: null,
            incompleteReasonOther: '',
            biggestAccomplishment: '',
            managerNote: '',
            workloadPulse: 'about_right',
            supersededNotice: null,
            clientUuid: null,
            showEarlyConfirm: false,
            earlyConfirmed: false,
            incompleteReasonOptions: [
                { value: 'higher_priority', text: 'A higher priority came up' },
                { value: 'waiting_on_someone', text: 'Waiting on someone else' },
                { value: 'larger_than_expected', text: 'Took longer than expected' },
                { value: 'blocked', text: 'Blocked' },
                { value: 'other', text: 'Other' },
            ],
            workloadPulseOptions: [
                { value: 'too_much', text: 'Too much', emoji: '😓' },
                { value: 'about_right', text: 'About right', emoji: '🙂' },
                { value: 'comfortable', text: 'Comfortable', emoji: '😊' },
            ],
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        internalDialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            },
        },
        dialogTitle() {
            const status = this.sodEodStatus();
            return status && status.eod_submitted ? 'View End of Day' : 'End of Day';
        },
        isAdmin() {
            return !!this.get_user && this.get_user.role_id === 1;
        },
        // Mirrors the Tracking App's "Remind me later" vs "Cancel" label, which
        // only ever frames dismissal as a snooze when EOD is required.
        cancelLabel() {
            const status = this.sodEodStatus();
            return status && status.eod_required ? 'Remind me later' : 'Cancel';
        },
        prefill() {
            return this.todayEodPrefill() || {};
        },
        plannedTasks() {
            return this.prefill.planned_tasks || [];
        },
        plannedCount() {
            return this.prefill.planned_count ?? this.plannedTasks.length;
        },
        completedCount() {
            return this.prefill.completed_count ?? this.plannedTasks.filter((t) => t.is_completed).length;
        },
        progressPercent() {
            if (!this.plannedCount) return 0;
            return Math.min(100, Math.round((this.completedCount / this.plannedCount) * 100));
        },
        // Nothing to explain when there was nothing left incomplete — only
        // relevant once at least one task was actually planned.
        allTasksCompleted() {
            return this.plannedCount > 0 && this.completedCount >= this.plannedCount;
        },
        incompleteCount() {
            return Math.max(0, this.plannedCount - this.completedCount);
        },
        incompleteReasonQuestion() {
            const suffix = this.incompleteCount === 1 ? 'task' : 'tasks';
            return `What prevented you from completing the remaining ${suffix}?`;
        },
        isEarlySubmission() {
            const status = this.sodEodStatus();
            if (!status || status.eod_submitted) return false;
            const promptHour = status.eod_prompt_hour ?? 17;
            return new Date().getHours() < promptHour;
        },
    },
    watch: {
        value(open) {
            if (open) {
                this.resetForm();
            }
        },
    },
    methods: {
        generateUuid() {
            if (window.crypto && window.crypto.randomUUID) {
                return window.crypto.randomUUID();
            }
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                const r = (Math.random() * 16) | 0;
                const v = c === 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        },
        formatTrackedTime(seconds) {
            const total = Number(seconds) || 0;
            const hours = Math.floor(total / 3600);
            const minutes = Math.floor((total % 3600) / 60);
            if (hours === 0 && minutes === 0) return '0m';
            return `${hours ? `${hours}h ` : ''}${minutes}m`.trim();
        },
        // Clicking a planned task toggles its real completion status (same
        // complete/reopen pattern as ScheduleView's calendar checkbox) —
        // optimisticUpdateTask already updates personalTasks/assignedTasks,
        // so this reflects in the main task list too. Reverts this row (and,
        // via optimisticUpdateTask's own rollback, the main list) if the API
        // call fails.
        async toggleTaskCompletion(task) {
            if (task.isToggling) return;
            const wasCompleted = task.is_completed;
            const previousStatusId = task.status_id;
            const newStatusId = task.status_id === 4 ? 1 : 4;
            const fullTask = this.findTaskInArrays(task.task_id);

            task.isToggling = true;
            task.is_completed = !wasCompleted;
            task.status_id = newStatusId;
            // completedCount/progressPercent read prefill.completed_count
            // (a fixed number from the server), not a derived count off
            // plannedTasks — keep it in sync with this row's flip too.
            if (this.prefill.completed_count !== undefined && this.prefill.completed_count !== null) {
                this.prefill.completed_count = Math.max(0, this.prefill.completed_count + (wasCompleted ? -1 : 1));
            }

            try {
                await this.optimisticUpdateTask(
                    task.task_id,
                    { status_id: newStatusId },
                    async () => {
                        const response = await this.$axios.post(`api/tasks/update_task/${task.task_id}`, {
                            company_id: fullTask ? fullTask.company_id : undefined,
                            status_id: newStatusId,
                        });
                        if (!response.data.response) {
                            throw new Error(response.data.message);
                        }
                        return response.data;
                    }
                );
            } catch (error) {
                task.is_completed = wasCompleted;
                task.status_id = previousStatusId;
                if (this.prefill.completed_count !== undefined && this.prefill.completed_count !== null) {
                    this.prefill.completed_count = Math.max(0, this.prefill.completed_count + (wasCompleted ? 1 : -1));
                }
                this.$toast.error(error.message || 'Failed to update task');
            } finally {
                task.isToggling = false;
            }
        },
        async resetForm() {
            this.supersededNotice = null;
            this.showEarlyConfirm = false;
            this.earlyConfirmed = false;
            this.clientUuid = this.generateUuid();
            this.loading = true;
            try {
                await this.refreshTodayEodPrefill();
            } finally {
                this.loading = false;
            }
            const existing = this.prefill;
            this.incompleteReason = existing.incomplete_reason || null;
            this.incompleteReasonOther = existing.incomplete_reason_other || '';
            this.biggestAccomplishment = existing.biggest_accomplishment || '';
            this.managerNote = existing.manager_note || '';
            this.workloadPulse = existing.workload_pulse || 'about_right';
        },
        dismiss() {
            this.internalDialog = false;
            this.$emit('dismissed');
        },
        viewHistory() {
            this.dismiss();
            this.$router.push({ path: '/sod-eod/history' });
        },
        // Admin-only support/testing tool — deletes today's EOD row
        // entirely, not a resubmission. Server enforces the admin check too.
        async clearData() {
            if (!window.confirm("Delete today's End of Day submission? This can't be undone.")) {
                return;
            }
            this.clearing = true;
            try {
                await this.clearEodAction();
                this.$toast.success('End of Day data cleared');
                await this.resetForm();
            } catch (error) {
                console.error('Error clearing EOD:', error);
                this.$toast.error(error?.response?.data?.message || error.message || 'Failed to clear End of Day');
            } finally {
                this.clearing = false;
            }
        },
        handleSubmitClick() {
            if (this.isEarlySubmission && !this.earlyConfirmed) {
                this.showEarlyConfirm = true;
                return;
            }
            this.doSubmit();
        },
        confirmEarly() {
            this.earlyConfirmed = true;
            this.showEarlyConfirm = false;
            this.doSubmit();
        },
        async doSubmit() {
            this.saving = true;
            try {
                const payload = {
                    work_date: this.sodEodStatus()?.work_date,
                    timezone: this.sodEodStatus()?.timezone,
                    client_uuid: this.clientUuid,
                    // Nothing to explain when everything planned got done —
                    // sent as null regardless of any leftover selection from
                    // before the last task was checked off.
                    incomplete_reason: this.allTasksCompleted ? null : this.incompleteReason,
                    incomplete_reason_other: (!this.allTasksCompleted && this.incompleteReason === 'other')
                        ? (this.incompleteReasonOther.trim() || null) : null,
                    biggest_accomplishment: this.biggestAccomplishment.trim() || null,
                    manager_note: this.managerNote.trim() || null,
                    workload_pulse: this.workloadPulse,
                };
                const result = await this.submitEodAction(payload);
                if (result && result.applied === false) {
                    this.supersededNotice = result;
                    this.$toast.info('A newer End of Day submission already exists');
                    const current = result.eod_submission || {};
                    this.incompleteReason = current.incomplete_reason || null;
                    this.incompleteReasonOther = current.incomplete_reason_other || '';
                    this.biggestAccomplishment = current.biggest_accomplishment || '';
                    this.managerNote = current.manager_note || '';
                    this.workloadPulse = current.workload_pulse || 'about_right';
                } else {
                    this.$toast.success('End of Day submitted');
                    this.internalDialog = false;
                    this.$emit('submitted', result);
                }
            } catch (error) {
                console.error('Error submitting EOD:', error);
                this.$toast.error(error?.response?.data?.message || error.message || 'Failed to submit End of Day');
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="scss">
// Shared SOD/EOD chrome (rounded card, circular sun/moon badge, tinted info
// box, pill actions) — kept in sync with the identical block in SodDialog.vue
// so either dialog renders correctly on its own, not just when both are mounted.
.sod-eod-dialog {
    border-radius: 16px;
}

// EOD-only: anchored near the top of the viewport (instead of Vuetify's
// default vertical-center) and given most of the viewport's height, so the
// form has room to grow before its internal scrollbar kicks in — vertical
// centering was wasting as much space above the dialog as it left below it.
.eod-dialog-content {
    align-self: flex-start;
    margin-top: -4vh;
    max-height: 97vh !important;
}

.sod-eod-card.v-card {
    border-radius: 16px !important;
    overflow: hidden;
}

.sod-eod-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 24px 24px 0;
}

.sod-eod-badge {
    width: 44px;
    height: 44px;
    min-width: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}

.sod-badge {
    background: #fff3d6;
}

.eod-badge {
    background: #ede7f6;
}

.sod-eod-heading {
    flex: 1 1 auto;
    min-width: 0;
    padding-top: 2px;
}

.sod-eod-title {
    font-size: 18px;
    font-weight: 700;
    color: #222;
}

.sod-eod-subtitle {
    font-size: 13px;
    color: #666;
    margin-top: 2px;
}

.sod-eod-close {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #888;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        background: #f2f3f5;
    }
}

.sod-eod-body {
    padding: 16px 24px 8px !important;
}

.sod-eod-info-box {
    border-radius: 8px;
    padding: 14px;
    margin-bottom: 16px;
}

.sod-info-box {
    background: #eaf7ee;

    .sod-eod-info-title {
        color: #1b5e20;
    }

    .sod-eod-info-text {
        color: #3c6b45;
    }
}

.eod-info-box {
    background: #eaf2fe;

    .sod-eod-info-title {
        color: #1a3e72;
    }
}

.sod-eod-info-title {
    font-weight: 700;
    font-size: 13px;
    margin-bottom: 4px;
    color: #222;
}

.sod-eod-info-text {
    font-size: 12px;
    color: #555;
}

.sod-eod-link {
    background: none;
    border: none;
    color: #2e9e5b;
    font-weight: 600;
    font-size: 13px;
    padding: 0;
    cursor: pointer;
    display: inline-block;

    &:hover {
        color: #1b5e20;
    }
}

.sod-eod-actions {
    padding: 8px 24px 24px !important;
}

.sod-eod-btn-primary.v-btn {
    background-color: #2e9e5b !important;
    color: #fff !important;
    text-transform: none;
    border-radius: 8px;
    font-weight: 600;
}

.sod-eod-btn-secondary.v-btn {
    background-color: #f1f1f3 !important;
    color: #333 !important;
    text-transform: none;
    border-radius: 8px;
    box-shadow: none !important;
    margin-right: 8px;
}

.tier-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 8px;
}
</style>

<style lang="scss" scoped>
.eod-progress-count {
    font-weight: 700;
    font-size: 13px;
    color: #1a3e72;
}

.eod-progress-percent {
    font-weight: 700;
    font-size: 13px;
    color: #1a3e72;
}

.eod-task-list {
    // Capped to ~4 visible rows, matching the Tracking App's EOD dialog —
    // the only scrolling surface in either dialog's task list.
    max-height: 136px;
    overflow-y: auto;
    overflow-x: hidden;
}

.eod-task-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    margin: 0 -8px;
    border-radius: 6px;
    cursor: pointer;

    &:hover {
        background: #f2f3f5;
    }

    .eod-task-name {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.eod-task-dot {
    width: 18px;
    height: 18px;
    min-width: 18px;
    border-radius: 50%;
    border: 1.5px solid #bdbdbd;
    display: flex;
    align-items: center;
    justify-content: center;

    &.done {
        border-color: #2e9e5b;
        background: #2e9e5b;
    }
}

.eod-reason-group ::v-deep .v-label {
    font-size: 13px;
}

.eod-reason-group ::v-deep .v-input--radio-group__input {
    flex-direction: column;
}

.eod-pulse-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
}

.eod-pulse-option {
    flex: 1 1 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fff;
    padding: 10px 8px;
    font-size: 13px;
    color: #333;
    cursor: pointer;

    &:hover {
        border-color: #bdbdbd;
    }

    &.selected {
        background: #e3f2fd;
        border-color: #42a5f5;
    }
}

.eod-pulse-emoji {
    font-size: 16px;
}
</style>
